import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const BlogCard = ({ 
  image, 
  date, 
  category, 
  title, 
  excerpt 
}: { 
  image: string; 
  date: string; 
  category: string; 
  title: string; 
  excerpt: string;
}) => {
  const { t } = useTranslation();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="group"
    >
      <div className="overflow-hidden mb-8">
        <motion.img 
          src={image} 
          alt={title} 
          className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div className="space-y-4">
        <div className="flex items-center space-x-4 text-sm text-[#c9ab81]">
          <span className="font-serif">{date}</span>
          <span className="text-gray-600">•</span>
          <span className="uppercase tracking-wider text-xs">{category}</span>
        </div>
        <h3 className="text-2xl font-serif group-hover:text-[#c9ab81] transition-colors duration-300">
          {title}
        </h3>
        <p className="text-gray-400 leading-relaxed">{excerpt}</p>
        <div className="pt-4">
          <div className="inline-block relative">
            <span className="text-[#c9ab81] font-serif">{t('blog.readMore')}</span>
            <span className="absolute bottom-0 left-0 w-full h-px bg-[#c9ab81] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Blog = () => {
  const { t, i18n } = useTranslation();

  const posts = {
    fr: [
      {
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113219/1739112803973_ta9cs1.jpg",
        date: "15 Mars 2024",
        category: "BIEN-ÊTRE",
        title: "L'Art de la Reconnexion à Soi",
        excerpt: "Découvrez comment nos retraites créent un espace sacré pour vous reconnecter à votre essence et retrouver votre équilibre intérieur."
      },
      {
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113202/1739112803057_ndnvhp.jpg",
        date: "12 Mars 2024",
        category: "TRANSFORMATION",
        title: "Le Pouvoir du Lâcher-Prise",
        excerpt: "Explorez les techniques et pratiques qui vous permettront de vous libérer de vos blocages et d'embrasser votre plein potentiel."
      },
      {
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113194/1736490672772_2_ffkp1w.jpg",
        date: "10 Mars 2024",
        category: "DÉVELOPPEMENT PERSONNEL",
        title: "Cultiver sa Confiance au Quotidien",
        excerpt: "Apprenez les rituels et habitudes qui vous aideront à renforcer votre confiance en vous et à rayonner dans tous les aspects de votre vie."
      }
    ],
    en: [
      {
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113219/1739112803973_ta9cs1.jpg",
        date: "March 15, 2024",
        category: "WELLNESS",
        title: "The Art of Self-Connection",
        excerpt: "Discover how our retreats create a sacred space for you to reconnect with your essence and restore your inner balance."
      },
      {
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113202/1739112803057_ndnvhp.jpg",
        date: "March 12, 2024",
        category: "TRANSFORMATION",
        title: "The Power of Letting Go",
        excerpt: "Explore the techniques and practices that will help you release your blockages and embrace your full potential."
      },
      {
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113194/1736490672772_2_ffkp1w.jpg",
        date: "March 10, 2024",
        category: "PERSONAL DEVELOPMENT",
        title: "Cultivating Daily Confidence",
        excerpt: "Learn the rituals and habits that will help you strengthen your self-confidence and shine in all aspects of your life."
      }
    ]
  };

  const currentPosts = posts[i18n.language as keyof typeof posts] || posts.fr;

  return (
    <div className="max-w-7xl mx-auto py-32 px-4">
      <motion.div 
        className="text-center mb-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-5xl font-serif mb-6">
          {t('blog.title')} <span className="text-[#c9ab81] italic">{t('blog.subtitle')}</span>
        </h2>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
          {t('blog.description')}
        </p>
      </motion.div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        {currentPosts.map((post, index) => (
          <BlogCard key={index} {...post} />
        ))}
      </div>
    </div>
  );
};

export default Blog;